import React, { useState, useEffect } from 'react';
import fetchApi from '../utils/api';
import CouponForm from './CouponForm';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import SearchBar from '../components/ui/SearchBar';
import PageSkeleton from '../components/ui/PageSkeleton';
import DataTable from '../components/ui/DataTable';
import { usePermissionContext } from '../contexts/PermissionContext';


const CouponList = () => {
    const { hasPermission, user } = usePermissionContext();
    const currentUserRole = user?.role || JSON.parse(sessionStorage.getItem('user'))?.role;
    const isSuperAdmin = currentUserRole === 'SUPER_ADMIN';

    const canAdd = isSuperAdmin || hasPermission('Coupon', 'add');
    const canUpdate = isSuperAdmin || hasPermission('Coupon', 'update');
    const canDelete = isSuperAdmin || hasPermission('Coupon', 'delete');

    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingCoupon, setEditingCoupon] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        fetchCoupons();
    }, []);

    const fetchCoupons = async () => {
        try {
            setLoading(true);
            const data = await fetchApi('/coupon');
            setCoupons(data);
        } catch (error) {
            console.error('Error fetching coupons:', error);
            toast.error('Failed to load coupons');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await fetchApi(`/coupon/${id}`, { method: 'DELETE' });
                toast.success('Coupon deleted successfully');
                fetchCoupons();
            } catch (error) {
                toast.error(error.message || 'Failed to delete coupon');
            }
        }
    };

    const handleToggleStatus = async (coupon) => {
        try {
            await fetchApi(`/coupon/${coupon._id}`, {
                method: 'PUT',
                body: JSON.stringify({ isActive: !coupon.isActive })
            });
            toast.success(`Coupon ${coupon.isActive ? 'Deactivated' : 'Activated'}`);
            fetchCoupons();
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const filteredCoupons = coupons.filter(c =>
        c.couponCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.couponName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalEntries = filteredCoupons.length;
    const totalPages = Math.ceil(totalEntries / pageSize) || 1;
    const pagedCoupons = filteredCoupons.slice((page - 1) * pageSize, page * pageSize);

    if (showForm) {
        return (
            <CouponForm
                coupon={editingCoupon}
                onCancel={() => { setShowForm(false); setEditingCoupon(null); }}
                onSuccess={() => { setShowForm(false); setEditingCoupon(null); fetchCoupons(); }}
            />
        );
    }

    if (loading) {
        return <PageSkeleton />;
    }

    const columns = [
        { key: 'couponCode', label: 'CODE', render: (item) => <strong style={{ color: '#1e3a8a' }}>{item.couponCode}</strong> },
        { key: 'couponName', label: 'NAME' },
        { key: 'discount', label: 'DISCOUNT', render: (item) => item.discountType === 'percentage' ? `${item.discountValue}% Off` : `₹${item.discountValue} Off` },
        { 
            key: 'validity', 
            label: 'VALIDITY', 
            render: (item) => (
                <div style={{ fontSize: '12px' }}>
                    <div>From: {new Date(item.validFrom).toLocaleDateString('en-IN')}</div>
                    <div>To: {new Date(item.validTo).toLocaleDateString('en-IN')}</div>
                </div>
            ) 
        },
        {
            key: 'status',
            label: 'STATUS',
            render: (item) => {
                const isExpired = new Date(item.validTo) < new Date().setHours(0, 0, 0, 0);
                const isActive = item.isActive && !isExpired;
                return (
                    <span
                        onClick={() => canUpdate && handleToggleStatus(item)}
                        className={`status-indicator ${isActive ? 'active' : 'inactive'}`}
                        style={{ cursor: canUpdate ? 'pointer' : 'default' }}
                    >
                        {isActive ? 'Active' : 'Inactive'}
                    </span>
                );
            }
        },
        ...((canUpdate || canDelete) ? [{
            key: 'actions',
            label: 'ACTIONS',
            render: (item) => (
                <div className="list-page-action-icons">
                    {canUpdate && (
                        <button
                            className="list-page-icon-btn"
                            title="Edit"
                            onClick={() => { setEditingCoupon(item); setShowForm(true); }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                        </button>
                    )}
                    {canDelete && (
                        <button
                            className="list-page-icon-btn"
                            title="Delete"
                            onClick={() => handleDelete(item._id)}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                        </button>
                    )}
                </div>
            )
        }] : [])
    ];

    return (
        <div className="list-page-container">
            <div className="list-page-header">
                <h1 className="list-page-title">Discount Coupons</h1>
                <div className="list-page-action-buttons">
                    {canAdd && (
                        <button className="list-page-btn list-page-btn-add" onClick={() => setShowForm(true)}>
                            + CREATE COUPON
                        </button>
                    )}
                </div>
            </div>

            <DataTable
                columns={columns}
                data={pagedCoupons}
                loading={loading}
                emptyMessage="No coupons found"
                page={page}
                pageSize={pageSize}
                totalEntries={totalEntries}
                totalPages={totalPages}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                searchBar={
                    <SearchBar
                        placeholder="Search by code or name..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(1);
                        }}
                    />
                }
            />
        </div>
    );
};

export default CouponList;
